import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthContext } from '../contexts/AuthContextProvider';
import { useApi } from '../api';
import {
    CarrierViewRequest,
    AuditLogRequest,
    CreateChangeRequestPayload,
    ChangeRequestActionPayload,
} from '../api/admin-api';

export function useCarrierViewClassic(params: CarrierViewRequest) {
    const { getAuthToken } = useAuthContext();
    const { adminApi } = useApi();
    return useQuery({
        queryKey: ['admin', 'carrier-view-classic', params],
        queryFn: () => adminApi.getCarrierViewClassic(params, getAuthToken()),
    });
}

export function useCarrierViewUniversal(params: CarrierViewRequest) {
    const { getAuthToken } = useAuthContext();
    const { adminApi } = useApi();
    return useQuery({
        queryKey: ['admin', 'carrier-view-universal', params],
        queryFn: () => adminApi.getCarrierViewUniversal(params, getAuthToken()),
    });
}

export type AppetiteView = 'classic' | 'universal';

export function useCarrierViewById(id: number | null, view: AppetiteView = 'classic') {
    const { getAuthToken } = useAuthContext();
    const { adminApi } = useApi();
    return useQuery({
        queryKey: ['admin', 'carrier-view', view, id],
        queryFn: () => view === 'universal'
            ? adminApi.getCarrierViewUniversalById(id!, getAuthToken())
            : adminApi.getCarrierViewById(id!, getAuthToken()),
        enabled: id !== null,
        retry: false,
    });
}

export function useCarrierViewByIds(ids: number[], view: AppetiteView = 'classic') {
    const { getAuthToken } = useAuthContext();
    const { adminApi } = useApi();
    return useQuery({
        queryKey: ['admin', 'carrier-view-bulk', view, ids],
        queryFn: async () => {
            const token = getAuthToken();
            return view === 'universal'
                ? adminApi.getCarrierViewUniversalByIds(ids, token)
                : adminApi.getCarrierViewClassicByIds(ids, token);
        },
        enabled: ids.length > 0,
        staleTime: 60_000,
        retry: false,
    });
}

export function useChangeRequests(status: string) {
    const { getAuthToken } = useAuthContext();
    const { adminApi } = useApi();
    return useQuery({
        queryKey: ['admin', 'change-requests', status],
        queryFn: () => adminApi.getChangeRequests(status, getAuthToken()),
    });
}

export function useCreateChangeRequest() {
    const { getAuthToken } = useAuthContext();
    const { adminApi } = useApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (payload: CreateChangeRequestPayload) =>
            adminApi.createChangeRequest(payload, getAuthToken()),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'change-requests'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'carrier-view-classic'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'carrier-view-universal'] });
        },
    });
}

export function useSubmitChangeRequest() {
    const { getAuthToken } = useAuthContext();
    const { adminApi } = useApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, payload }: { id: number; payload: ChangeRequestActionPayload }) =>
            adminApi.submitChangeRequest(id, payload, getAuthToken()),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['admin', 'change-requests'] });
        },
    });
}

export function useReviewChangeRequest() {
    const { getAuthToken } = useAuthContext();
    const { adminApi } = useApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, payload }: { id: number; payload: ChangeRequestActionPayload }) =>
            adminApi.reviewChangeRequest(id, payload, getAuthToken()),
        onSuccess: () => {
            queryClient.removeQueries({ queryKey: ['admin', 'change-requests'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'change-requests'], refetchType: 'all' });
        },
    });
}

export function useApproveChangeRequest() {
    const { getAuthToken } = useAuthContext();
    const { adminApi } = useApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, payload }: { id: number; payload: ChangeRequestActionPayload }) =>
            adminApi.approveChangeRequest(id, payload, getAuthToken()),
        onSuccess: () => {
            queryClient.removeQueries({ queryKey: ['admin', 'change-requests'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'change-requests'], refetchType: 'all' });
        },
    });
}

export function useRejectChangeRequest() {
    const { getAuthToken } = useAuthContext();
    const { adminApi } = useApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, payload }: { id: number; payload: ChangeRequestActionPayload }) =>
            adminApi.rejectChangeRequest(id, payload, getAuthToken()),
        onSuccess: () => {
            queryClient.removeQueries({ queryKey: ['admin', 'change-requests'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'change-requests'], refetchType: 'all' });
        },
    });
}

export function useApplyChangeRequest() {
    const { getAuthToken } = useAuthContext();
    const { adminApi } = useApi();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, payload }: { id: number; payload: ChangeRequestActionPayload }) =>
            adminApi.applyChangeRequest(id, payload, getAuthToken()),
        onSuccess: () => {
            // Remove cache then immediately force refetch of all change-request queries
            // (active and inactive tabs) so all tab counts and lists reflect the new state.
            queryClient.removeQueries({ queryKey: ['admin', 'change-requests'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'change-requests'], refetchType: 'all' });
            queryClient.invalidateQueries({ queryKey: ['admin', 'carrier-view-classic'] });
            queryClient.invalidateQueries({ queryKey: ['admin', 'carrier-view-universal'] });
        },
    });
}

export function useAuditLogs(params: AuditLogRequest, enabled = true) {
    const { getAuthToken } = useAuthContext();
    const { adminApi } = useApi();
    return useQuery({
        queryKey: ['admin', 'audit-logs', params],
        queryFn: () => adminApi.getAuditLogs(params, getAuthToken()),
        enabled,
    });
}

export function useAuditStatistics() {
    const { getAuthToken } = useAuthContext();
    const { adminApi } = useApi();
    return useQuery({
        queryKey: ['admin', 'audit-statistics'],
        queryFn: () => adminApi.getAuditStatistics(getAuthToken()),
    });
}

export function useAuditExport() {
    const { getAuthToken } = useAuthContext();
    const { adminApi } = useApi();
    return useMutation({
        mutationFn: (params: AuditLogRequest) =>
            adminApi.getAuditExport(params, getAuthToken()),
    });
}

export function useReferenceCarriers() {
    const { getAuthToken } = useAuthContext();
    const { adminApi } = useApi();
    return useQuery({
        queryKey: ['admin', 'reference-carriers'],
        queryFn: () => adminApi.getReferenceCarriers(getAuthToken()),
    });
}

export function useReferenceLinesOfBusiness() {
    const { getAuthToken } = useAuthContext();
    const { adminApi } = useApi();
    return useQuery({
        queryKey: ['admin', 'reference-lobs'],
        queryFn: () => adminApi.getReferenceLinesOfBusiness(getAuthToken()),
    });
}

export function useReferenceStates() {
    const { getAuthToken } = useAuthContext();
    const { adminApi } = useApi();
    return useQuery({
        queryKey: ['admin', 'reference-states'],
        queryFn: () => adminApi.getReferenceStates(getAuthToken()),
    });
}

export function useReferenceKeywords(lineOfBusiness: string) {
    const { getAuthToken } = useAuthContext();
    const { adminApi } = useApi();
    return useQuery({
        queryKey: ['admin', 'reference-keywords', lineOfBusiness],
        queryFn: () => adminApi.getReferenceKeywords(lineOfBusiness, getAuthToken()),
        enabled: !!lineOfBusiness,
    });
}
